/* eslint-disable @typescript-eslint/no-empty-function */
import React, {ReactElement, useContext, useEffect, useState} from "react";
import {useHistory, useParams} from "react-router-dom";
import useAppStore from "../../hooks/useAppStore";

import AnalystSectionTabs, {AnalystSections} from "../../components/analyst/section-tabs/AnalystSectionTabs";
import * as styles from "./styles.module.scss";


import {Payroll} from "../payroll/Payroll";
import AnalystRenovations from "./tab-renovations/AnalystRenovations";
import {useAnalystDashboardProperties} from "./AnalystDashboardProperties";

import {RenderIfAuthorized, RenderIfReadOnly} from "../../authorization/RenderIfAuthorized";
import {Authorizations} from "../../authorization/Authorizations";
import {AuthorizationContext} from "../../authorization/AuthorizationContext";
import {ThemeProvider} from "styled-components";

import ReadOnlyHeader from "../../components/navbar/ReadOnlyHeader";
import {useProperties} from "../../contexts/properties/PropertiesContext";
import {ViziblyTheme} from "../../styles/zendesk-garden/ViziblyZDGTheme";
import {useGetUserPropertiesForecastLocksLazyQuery} from "../../__generated__/generated_types";
import {TLockedProperties} from "../bulk-update/BulkUpdate";
import {RenovationCategories} from "./tab-renovation-categories/RenovationCategories";
import {toast} from "react-toastify";
import {AuthContext} from "../../contexts/AuthContext";

interface SectionTabData {
    section: AnalystSections;
    requiredAuthorization: Authorizations | null;
    defaultOrder: number
}
const SectionTabMap:Record<string, SectionTabData> = {
    'renovations': {
        section: AnalystSections.Renovations,
        requiredAuthorization: null,
        defaultOrder: 4
    },
    'renovations-categories': {
        section: AnalystSections.RenovationCategories,
        requiredAuthorization: null,
        defaultOrder: 6
    },
    'payroll': {
        section: AnalystSections.Payroll,
        requiredAuthorization: Authorizations.PlanningHubPayroll,
        defaultOrder: 3
    },
};

function getInitialSection(tab: string, hasAuthorization: (auth: Authorizations) => boolean): AnalystSections {
    const section = SectionTabMap[tab];
    if(section && (section.requiredAuthorization === null || hasAuthorization(section.requiredAuthorization))) {
        return section.section;
    }

    // Otherwise try to find the appropriate default for the user.
    const allowedSections = Object.values(SectionTabMap)
        .sortBy(st => st.defaultOrder)
        .filter(st => st.requiredAuthorization === null || hasAuthorization(st.requiredAuthorization));
    if(allowedSections.length > 0 && allowedSections[0]) {
        return allowedSections[0].section;
    }

    // We should never reach this point, but if we do
    // it will never load anything.
    return AnalystSections.Renovations;
}

export default function AnalystDashboard(): ReactElement {
    const history = useHistory();
    const appStore = useAppStore();
    const {properties, currentProperty} = useProperties();
    const propertiesFilter = useAnalystDashboardProperties();
    const {hasAuthorization} = useContext(AuthorizationContext);
    const {budgetYear} = useContext(AuthContext);

    const { tab } = useParams() as { tab: string };
    const showingSection = getInitialSection(tab, hasAuthorization);

    const [sectionReady, setSectionReady] = useState<Record<AnalystSections, boolean>>({
        [AnalystSections.Payroll]: false,
        [AnalystSections.Renovations]: false,
        [AnalystSections.RenovationCategories]: false
    });
    const [lockedProperties, setLockedProperties] = useState<TLockedProperties>({reforecastLocked: [], budgetLocked: []});

    const [getPropertyLocks, {data: propertyLocks, loading: propertyLocksLoading}] = useGetUserPropertiesForecastLocksLazyQuery({
        fetchPolicy: "no-cache",
    });


    useEffect(() => {
        if (budgetYear === undefined) {
            return;
        }

        getPropertyLocks({
            variables: {
                budgetYear,
            }
        });
    }, [budgetYear]);

    useEffect(() => {
        if (propertyLocksLoading || !propertyLocks?.userPropertiesForecastLocks) {
            return;
        }

        const {budgetLocked, reforecastLocked} = propertyLocks.userPropertiesForecastLocks;
        setLockedProperties({budgetLocked, reforecastLocked});
    }, [propertyLocksLoading, propertyLocks?.userPropertiesForecastLocks]);

    //////////////////////////////
    // BEGIN: Defer Load of sections until requested.
    // Notes:
    // 1. This is part of incremental migration towards Planning Hub v2
    // 2. Planning Hub v2 has a different, more consolidated, approach that's handled in a context
    /////////////////////////////
    const [sectionVisited, setSectionVisited] = useState<Record<AnalystSections, boolean>>({
        [AnalystSections.Payroll]: false,
        [AnalystSections.Renovations]: false,
        [AnalystSections.RenovationCategories]: false
    });
    useEffect(
            ()=>{
                setSectionVisited( visitedState => {
                    return {
                        ...visitedState,
                        [showingSection]: true,
                    };
                });
            },
            [showingSection]);
    //////////////////////////////
    // END: Defer Load of sections until requested.
    //////////////////////////////

    //////////////////////////////
    // BEGIN: Unit-Level Modeling
    /////////////////////////////
    const [hasUnitLevelModeling, setHasUnitLevelModeling] = useState<boolean>(false);
    useEffect(() => {
        if(!currentProperty) {
            setHasUnitLevelModeling(false);
            return;
        }

        setHasUnitLevelModeling(currentProperty.unitLevelModelingEnabled);

        if (currentProperty.unitLevelModelingEnabled && (showingSection == AnalystSections.Renovations || showingSection == AnalystSections.RenovationCategories)) {
            history.push("/hub/payroll");
            toast.warning(`${currentProperty.name} does not have access to Renovations`);
        }
    }, [currentProperty]);
    /////////////////////////////
    // END: Unit-Level Modeling
    /////////////////////////////

    useEffect(
        () => {
            // Dispells the "Vizibly is starting" graphic
            appStore.set({ isLoading: false });
            return () => {
            };
        },
        []);

    function onSectionReadyReport(sectionId:AnalystSections, isReady:boolean):void{
        setSectionReady(previous => {
            return {
                ...previous,
                ...{[sectionId]: isReady }
            };
        });
    }

    return (
        <ThemeProvider theme={ViziblyTheme}>
            <section className={styles.dashboard}>
                <RenderIfReadOnly>
                    <ReadOnlyHeader />
                </RenderIfReadOnly>
                <div className={styles.mainHeader}>
                    <div className={styles.viewTitle}>Planning Hub</div>
                </div>
                <AnalystSectionTabs
                    showingSection={showingSection}
                    propertiesFilter={propertiesFilter}
                    hasUnitLevelModeling={hasUnitLevelModeling}
                />
                <div className={styles.bodyContent}>
                    {sectionVisited[AnalystSections.Renovations] && showingSection == AnalystSections.Renovations && budgetYear &&
                        <AnalystRenovations
                            setSectionReady={onSectionReadyReport}
                            propertiesFilter={propertiesFilter}
                            lockedProperties={lockedProperties}
                            budgetYear={budgetYear}
                            hideSpreadsheet={
                                showingSection != AnalystSections.Renovations ||
                                !sectionReady[AnalystSections.Renovations] ||
                                !propertiesFilter.isReady}/>
                    }
                    {sectionVisited[AnalystSections.RenovationCategories] && showingSection == AnalystSections.RenovationCategories && budgetYear &&
                        <RenovationCategories budgetYear={budgetYear} ready={() => {
                            onSectionReadyReport(AnalystSections.RenovationCategories, true);
                        }} />
                    }
                    {sectionVisited[AnalystSections.Payroll] && showingSection == AnalystSections.Payroll && budgetYear != undefined
                            && <RenderIfAuthorized required={Authorizations.PlanningHubPayroll}>
                        <Payroll
                            setSectionReady={onSectionReadyReport}
                            budgetYear={budgetYear}
                            lockedProperties={lockedProperties}
                            filterPropertyIds={propertiesFilter.isReady ? propertiesFilter.selectedProperties.map(p => p.id) : undefined}
                        />
                    </RenderIfAuthorized>}
                </div>
            </section>
        </ThemeProvider>
    );
}
